import { defineStore } from 'pinia';
import { useTutorialStore } from '@/stores/tutorials';
import { useShareStore } from '@/share/store';
import { useAccountsStore } from '@/accounts/store';
import { loadTermsAccepted, acceptCurrentTerms, loadUserSharingPermissions } from './api';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        // jwt is kept as a sync-readable snapshot of the Auth0 access token so that
        // components that read authStore.jwt (e.g. content-sharing-lib) continue to work.
        // Auth0 SDK is the source of truth; this field is a last-known-good mirror.
        jwt: null,
        user: {},
        termsAccepted: false,
        sharingPermissions: [],
    }),

    getters: {
        hasSharingPermission: ({ sharingPermissions }) => (permission) => {
            return sharingPermissions.includes(permission);
        },
    },

    actions: {
        setJwt(jwt) {
            this.jwt = jwt;
        },

        async loadTerms(appId) {
            try {
                const termsAccepted = await loadTermsAccepted(appId);

                this.termsAccepted = termsAccepted;
            } catch (e) {
                this.termsAccepted = false;
            }
        },

        async acceptTerms(appId) {
            await acceptCurrentTerms(appId);

            this.termsAccepted = true;
        },

        async loadSharingPermissions({ appId }) {
            const { permissions } = await loadUserSharingPermissions(appId);

            this.sharingPermissions = permissions;
        },

        resetTermsAccepted() {
            this.termsAccepted = false;
        },

        logout() {
            this.jwt = null;
            this.user = {};
            this.termsAccepted = false;
            this.sharingPermissions = [];
            useTutorialStore().reset();
            useAccountsStore().updateAppId('');
            useShareStore().setExportSourceAppId('');
        },

        reset() {
            this.$reset();
        },
    },

    persist: {
        paths: ['termsAccepted', 'sharingPermissions'],
    },
});
